// Layout-3 contract review checklist for one department row: every item it is
// responsible for, answered ✓ OK / ⚠ Issue / N/A. ⚠ Issue opens a small form
// that logs the concern as a contract issue (pre-filled with the item), so it
// flows through the resolve-with-the-issuer step. Rules: contractReviewItems.js.
import { useState } from "react";
import { REVIEW_ANSWERS, reviewChecklistState } from "../../config/contractReviewItems";

const COLORS = {
  ok: { bg: "#dcfce7", color: "#166534", border: "#86efac" },
  issue: { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" },
  na: { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
};

export default function ReviewChecklist({ review, items, canAnswer, onAnswer, onLogIssue, busy, compact = false }) {
  const [draft, setDraft] = useState(null); // { key, text, must }
  const state = reviewChecklistState(items);
  const forcedMust = review.Status === "Rejected";

  async function pick(item, answer) {
    const next = item.answer === answer ? null : answer;   // click again to clear
    await onAnswer(review, item, next);
    if (next === "issue") setDraft({ key: item.key, text: `${item.label}: `, must: true });
    else if (draft?.key === item.key) setDraft(null);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 4 : 5 }}>
      {items.map(item => (
        <div key={item.key}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ flex: "1 1 120px", fontSize: 12, color: "var(--text)", lineHeight: 1.3 }}>{item.label}</span>
            <span style={{ display: "flex", gap: 3 }}>
              {REVIEW_ANSWERS.map(a => {
                const on = item.answer === a.key;
                const c = COLORS[a.key];
                return (
                  <button key={a.key} type="button" title={a.title} disabled={!canAnswer || busy}
                    onClick={() => pick(item, a.key)}
                    style={{ padding: "2px 7px", fontSize: 11, fontWeight: 700, borderRadius: 100, whiteSpace: "nowrap",
                      cursor: canAnswer ? "pointer" : "default",
                      border: `1px solid ${on ? c.border : "var(--cs-border)"}`, background: on ? c.bg : "var(--surface)", color: on ? c.color : "var(--text3)",
                      opacity: !canAnswer && !on ? 0.5 : 1 }}>
                    {a.label}
                  </button>
                );
              })}
            </span>
          </div>
          {draft?.key === item.key && canAnswer && (
            <div style={{ margin: "5px 0 4px", padding: 8, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, display: "flex", flexDirection: "column", gap: 5 }}>
              <textarea rows={2} value={draft.text} autoFocus onChange={e => setDraft({ ...draft, text: e.target.value })}
                placeholder="What must change, or what the project team must watch"
                style={{ width: "100%", boxSizing: "border-box", fontSize: 12, padding: "5px 8px", border: "1px solid var(--cs-border)", borderRadius: 5, fontFamily: "inherit", resize: "vertical", minHeight: 0 }} />
              <label style={{ fontSize: 11.5, color: "var(--text2)", display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={forcedMust || draft.must} disabled={forcedMust} onChange={e => setDraft({ ...draft, must: e.target.checked })} />
                Must resolve with the issuer before signing
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" disabled={busy || draft.text.trim().length <= item.label.length + 1}
                  onClick={async () => { if (await onLogIssue(review, draft.text.trim(), forcedMust || draft.must)) setDraft(null); }}
                  style={{ padding: "4px 12px", fontSize: 12, fontWeight: 700, borderRadius: 5, border: "none", background: "#1e3a5f", color: "#fff", cursor: "pointer" }}>
                  Log issue
                </button>
                <button type="button" onClick={() => setDraft(null)} style={{ padding: "4px 12px", fontSize: 12, borderRadius: 5, border: "1px solid var(--cs-border)", background: "var(--surface)", cursor: "pointer" }}>Later</button>
              </div>
            </div>
          )}
        </div>
      ))}
      <div style={{ fontSize: 10.5, color: state.complete ? "#166534" : "var(--text3)", marginTop: 2 }}>
        {state.answered} of {state.total} answered{state.issues ? ` · ${state.issues} issue${state.issues === 1 ? "" : "s"}` : ""}
      </div>
    </div>
  );
}
